import type { ResolvedTheme } from '../preferences/models';

type VarMap = Record<string, string>;

const LIGHT: VarMap = {
    'panel-bg': '#FFFFFF',
    'panel-muted': '#F6F3FC',
    'page-bg': '#F2EEFB',
    'card-border': '#E7E1F4',
    'card-border-strong': '#D5CCEC',
    'outline-gray': '#C4B8DE',
    'dim-text': '#6B7080',

    accent: '#7C5CFF',
    'accent-hover': '#6644F0',
    'accent-bg': '#EEEAFB',
    'accent-bg-hover': '#DDD5F7',
    'accent-bg-strong': '#C4B5F0',
    'accent-text': '#4F36C2',

    'soft-shadow-sm': '0 1px 2px rgba(50, 30, 100, 0.05)',
    'soft-shadow-md': '0 4px 16px rgba(124, 92, 255, 0.08), 0 1px 2px rgba(50, 30, 100, 0.04)',
    'soft-shadow-lg': '0 14px 32px rgba(124, 92, 255, 0.16), 0 4px 10px rgba(50, 30, 100, 0.06)',
    'dialog-shadow': '0 12px 32px rgba(15, 23, 42, 0.32)',
    'side-panel-shadow': '-16px 0 40px rgba(15, 23, 42, 0.16)',

    'hover-tint': 'rgba(15, 23, 42, 0.04)',
    'focus-tint': 'rgba(15, 23, 42, 0.06)',

    'logged-pill-bg': '#E0F5EE',
    'pending-pill-bg': '#FAE8FF',
    'pending-pill-text': '#86198F',
    'pending-card-bg': '#FDF4FF',
    'issue-badge-bg': '#E0F5EE',
    'bug-badge-bg': '#FEE2E2',
    'request-badge-bg': '#DBEAFE',
    'holiday-bg': '#E5F8F5',
    'day-off-bg': '#DEE1F3',
    'non-working-bg': '#FAFAFA',
    'progress-track-bg': '#F1F5F9',
    'status-on-track-bg': '#DCFCE7',
    'status-on-track-text': '#15803D',
    'status-under-bg': '#EDEFEF',
    'status-under-text': '#5C6462',
    'status-over-bg': '#F1F4DC',
    'status-over-text': '#66771F',
    'selected-bg': '#EFF6FF',
    'add-bg': '#FFFBEB',
    'update-bg': '#EFF6FF',
    'remove-bg': '#FEF2F2',
    backdrop: 'rgba(0, 0, 0, 0.32)',
};

const DARK: VarMap = {
    'panel-bg': '#1B1B27',
    'panel-muted': '#23232F',
    'page-bg': '#0F0F18',
    'card-border': '#2D2D3D',
    'card-border-strong': '#3D3D4F',
    'outline-gray': '#555568',
    'dim-text': '#9090A0',

    accent: '#A78BFA',
    'accent-hover': '#8B71E8',
    'accent-bg': '#2D2545',
    'accent-bg-hover': '#3D3057',
    'accent-bg-strong': '#4D406B',
    'accent-text': '#C4B5FD',

    'soft-shadow-sm': '0 1px 2px rgba(0, 0, 0, 0.3)',
    'soft-shadow-md': '0 4px 16px rgba(0, 0, 0, 0.25), 0 1px 2px rgba(0, 0, 0, 0.2)',
    'soft-shadow-lg': '0 14px 32px rgba(0, 0, 0, 0.4), 0 4px 10px rgba(0, 0, 0, 0.2)',
    'dialog-shadow': '0 12px 32px rgba(0, 0, 0, 0.6)',
    'side-panel-shadow': '-16px 0 40px rgba(0, 0, 0, 0.5)',

    'hover-tint': 'rgba(255, 255, 255, 0.05)',
    'focus-tint': 'rgba(255, 255, 255, 0.08)',

    'logged-pill-bg': '#0F3D2E',
    'pending-pill-bg': '#3D1A4A',
    'pending-pill-text': '#E9A8F0',
    'pending-card-bg': '#1F0F2A',
    'issue-badge-bg': '#0F3D2E',
    'bug-badge-bg': '#3D1414',
    'request-badge-bg': '#14254D',
    'holiday-bg': '#163B38',
    'day-off-bg': '#1B1F3D',
    'non-working-bg': '#1A1A24',
    'progress-track-bg': '#2D2D3D',
    'status-on-track-bg': '#1A2D24',
    'status-on-track-text': '#86EFAC',
    'status-under-bg': '#282B2A',
    'status-under-text': '#B4BBB9',
    'status-over-bg': '#2B3012',
    'status-over-text': '#C3D45E',
    'selected-bg': '#1F2937',
    'add-bg': '#2D2614',
    'update-bg': '#1A2A3F',
    'remove-bg': '#3D1F1F',
    backdrop: 'rgba(0, 0, 0, 0.6)',
};

export const cssVarsFor = (mode: ResolvedTheme): Record<string, string> => {
    const vars = mode === 'dark' ? DARK : LIGHT;
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(vars)) {
        out[`--ttk-${key}`] = value;
    }
    return out;
};

export const cssVar = (key: keyof typeof LIGHT): string => `var(--ttk-${key})`;
